import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthDefaultData, useAuthModal, useAuthUser } from 'authOld/hooks';
import { IAuthDefaultData } from 'authOld/types';
import { login } from 'authOld/utils';
import { HeaderModalOptions, TRIGGER_MODAL_OPEN_PARAM } from 'coreOld/constants';
import { LogoType } from 'coreOld/enums';
import { useGetLogoPaths } from 'coreOld/hooks';
import { useNotification } from 'coreOld/hooks/useNotification';
import { DropdownOptions } from 'coreOld/types';
import { getOpenModalByTriggerParam, isEmptyObject } from 'coreOld/utils';

import HeaderView from './HeaderView';

interface IProps {
  isFixed?: boolean;
  hasSearchBar?: boolean;
  triggerSignInModal?: boolean;
  collapsedMenu?: boolean;
}

const dropdownInitialValues = {
  browse: false,
  pricing: false,
  company: false,
  profile: false,
};

const Header = ({
  isFixed = false,
  hasSearchBar = true,
  triggerSignInModal = false,
  collapsedMenu = false,
}: IProps) => {
  const [isSearchFocus, setIsSearchFocus] = useState(false);

  const { openedAuthModal, openAuthModal } = useAuthModal();
  const [openedDropdown, setOpenedDropdown] = useState<DropdownOptions>(dropdownInitialValues);
  const [searchParams, setSearchParams] = useSearchParams();
  const [authDefaultData, setAuthDefaultData] = useState<IAuthDefaultData>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user } = useAuthUser();
  const { logoPaths } = useGetLogoPaths();
  const { authDefaultData: authDefault } = useAuthDefaultData(
    !user && openedAuthModal === HeaderModalOptions.SET_UP_ACCOUNT
  );

  useEffect(() => {
    if (user && !isEmptyObject(searchParams)) {
      setSearchParams([]);

      return;
    }

    const triggerValue = searchParams.get(TRIGGER_MODAL_OPEN_PARAM);

    if (triggerValue) {
      openAuthModal(getOpenModalByTriggerParam(triggerValue));

      searchParams.delete(TRIGGER_MODAL_OPEN_PARAM);

      // Replace, carrying the state forward. A plain push dropped the page
      // PrivateRoute recorded in `state.from`, so a visitor sent here from a
      // deep link had already lost it before the modal opened — and left a
      // history entry that re-opened the modal on Back.
      setSearchParams(searchParams, { replace: true, state: location.state });
    }

    if (triggerSignInModal) {
      openAuthModal(HeaderModalOptions.SIGN_IN);
    }
  }, [location.state, openAuthModal, searchParams, setSearchParams, triggerSignInModal, user]);

  useEffect(() => {
    if (user) {
      return;
    }

    if (openedAuthModal === HeaderModalOptions.SET_UP_ACCOUNT) {
      setAuthDefaultData(authDefault);
    }
  }, [openedAuthModal, authDefault, user]);

  const toggleDropdown = (name: keyof DropdownOptions) => {
    setOpenedDropdown((prevState) => {
      return { ...dropdownInitialValues, [name]: !prevState[name] };
    });
  };

  const handleOnNext = async (step: HeaderModalOptions) => {
    switch (step) {
      case HeaderModalOptions.SET_UP_ACCOUNT:
        if (authDefaultData?.ssoEnabled && authDefaultData?.skipAddress) {
          await login(navigate, showNotification);

          break;
        }

        if (authDefaultData?.ssoEnabled) {
          openAuthModal(HeaderModalOptions.CREATE_LOCATION);

          break;
        }

        openAuthModal(HeaderModalOptions.CREATE_PASSWORD);

        break;
      case HeaderModalOptions.CREATE_PASSWORD:
        if (authDefaultData?.skipAddress) {
          await login(navigate, showNotification);

          break;
        }

        openAuthModal(HeaderModalOptions.CREATE_LOCATION);

        break;
      case HeaderModalOptions.CREATE_LOCATION:
        await login(navigate, showNotification);

        break;
      default:
        break;
    }
  };

  return (
    <HeaderView
      user={user}
      isFixed={isFixed}
      hasSearchBar={hasSearchBar}
      isSearchFocus={isSearchFocus}
      setIsSearchFocus={setIsSearchFocus}
      collapsedMenu={collapsedMenu}
      openedDropdown={openedDropdown}
      toggleDropdown={toggleDropdown}
      logoPath={logoPaths?.[LogoType.HEADER] || logoPaths?.[LogoType.SMALL]}
      onNext={handleOnNext}
    />
  );
};

export default Header;
